"use strict";
import { numberToHex } from "viem";
import { bsc } from "viem/chains";
import { isPaymasterFailure, isUserExecutionFailure } from "components/Web3Provider/privy/scwErrors";
const BSC_CHAIN_ID_HEX = numberToHex(bsc.id);
const EIP_5792_PENDING_STATUS = 100;
const EIP_5792_SUCCESS_STATUS = 200;
const EIP_5792_FAILURE_STATUS = 500;
function toBigIntValue(value) {
  if (value === void 0 || value === null || value === "0x" || value === "") {
    return 0n;
  }
  return typeof value === "bigint" ? value : BigInt(value);
}
function toCall(tx) {
  if (!tx.to) {
    throw new Error("Topaz ID smart wallet: transaction request is missing a `to` address");
  }
  return {
    to: tx.to,
    value: toBigIntValue(tx.value),
    data: tx.data ?? "0x"
  };
}
function asParamArray(params) {
  if (Array.isArray(params)) {
    return params;
  }
  return params === void 0 ? [] : [params];
}
export function createTopazScwProvider({
  client,
  readProvider,
  getSelfFundedSender
}) {
  const listeners = /* @__PURE__ */ new Map();
  let senderPromise;
  const resolveSelfFundedSender = () => {
    if (senderPromise === void 0) {
      senderPromise = (async () => {
        try {
          return getSelfFundedSender ? await getSelfFundedSender() : null;
        } catch {
          return null;
        }
      })();
    }
    return senderPromise;
  };
  const withSelfFundedFallback = async (err, fallback) => {
    if (!isPaymasterFailure(err) || isUserExecutionFailure(err)) {
      throw err;
    }
    const sender = await resolveSelfFundedSender();
    if (!sender) {
      throw err;
    }
    return fallback(sender);
  };
  const handleRequest = async ({
    method,
    params
  }) => {
    const args = asParamArray(params);
    switch (method) {
      case "eth_requestAccounts":
      case "eth_accounts": {
        const address = client.account?.address;
        return address ? [address] : [];
      }
      case "eth_chainId":
        return BSC_CHAIN_ID_HEX;
      case "net_version":
        return String(bsc.id);
      case "wallet_switchEthereumChain": {
        const target = args[0];
        if (target?.chainId && BigInt(target.chainId) !== BigInt(bsc.id)) {
          throw Object.assign(new Error("Topaz ID smart wallet only operates on BNB Chain"), { code: 4902 });
        }
        return null;
      }
      case "wallet_addEthereumChain":
        return null;
      // Gas params from the request are intentionally dropped — UserOperation gas and sponsorship are
      // computed by the bundler + paymaster. We forward only to/value/data, mirroring topaz-portal.
      case "eth_sendTransaction": {
        const tx = args[0] ?? {};
        const call = toCall(tx);
        try {
          return await client.sendTransaction(call);
        } catch (err) {
          return withSelfFundedFallback(err, (sender) => sender.sendTransaction(call));
        }
      }
      // EIP-5792 atomic batch → a single sponsored UserOperation (e.g. approve + swap in one action).
      case "wallet_sendCalls": {
        const batch = args[0] ?? {};
        const calls = (batch.calls ?? []).map(toCall);
        try {
          const hash = await client.sendTransaction({ calls });
          return { id: hash };
        } catch (err) {
          return { id: await withSelfFundedFallback(err, (sender) => sender.sendCalls(calls)) };
        }
      }
      // The batch id is the resulting transaction hash. A Kernel UserOperation is atomic by
      // construction, so report `atomic: true`; map the receipt to the 5792 status — pending when the
      // receipt isn't available yet (NOT confirmed), reverted on a 0x0 status, else confirmed.
      case "wallet_getCallsStatus": {
        const id = args[0];
        const receipt = await readProvider.request({
          method: "eth_getTransactionReceipt",
          params: [id]
        });
        let status;
        if (!receipt) {
          status = EIP_5792_PENDING_STATUS;
        } else if (receipt.status === "0x0") {
          status = EIP_5792_FAILURE_STATUS;
        } else {
          status = EIP_5792_SUCCESS_STATUS;
        }
        return {
          version: "2.0.0",
          id,
          chainId: BSC_CHAIN_ID_HEX,
          atomic: true,
          status,
          receipts: receipt ? [receipt] : []
        };
      }
      case "wallet_getCapabilities":
        return { [BSC_CHAIN_ID_HEX]: { atomic: { status: "supported" } } };
      case "personal_sign": {
        const message = args[0];
        return client.signMessage({ message: { raw: message } });
      }
      case "eth_sign": {
        const message = args[1];
        return client.signMessage({ message: { raw: message } });
      }
      case "eth_signTypedData_v4":
      case "eth_signTypedData": {
        const raw = args[1];
        const typedData = typeof raw === "string" ? JSON.parse(raw) : raw;
        return client.signTypedData(typedData);
      }
      default:
        return readProvider.request({ method, params: args });
    }
  };
  return {
    // The handler multiplexes by method string, so its return is `unknown`; assert to the precise
    // viem request type (the per-method return mapping can't be expressed by a single signature).
    request: handleRequest,
    on(event, listener) {
      const set = listeners.get(event) ?? /* @__PURE__ */ new Set();
      set.add(listener);
      listeners.set(event, set);
    },
    removeListener(event, listener) {
      listeners.get(event)?.delete(listener);
    }
  };
}
